import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Environment } from '@desbank/core/src/lib/environment';
import { CacheService } from '@desbank/core/src/lib/services/cache';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class APIConnector {

  private responseHeaders = new Subject<HttpHeaders>();
  public responseHeaders$ = this.responseHeaders.asObservable();

  constructor(
    private httpClient: HttpClient,
    private cacheService: CacheService,
    @Inject('environment') private env: Environment
  ) {}

  public get(endPoint: string, params?: any, headers?: HttpHeaders): Observable<any> {
    if (params && params.currentPage) {
      return this.getWithPagination(endPoint, params, headers);
    }

    return this.getWithoutPagination(endPoint, params, headers);
  }

  public post(endPoint: string, params: any, headers?: HttpHeaders): Observable<any> {
    return this.httpClient.post(this.buildEndPoint(endPoint), params, {headers});
  }

  public postBlob(endPoint: string, params: any, headers?: HttpHeaders): Observable<any> {
    return this.httpClient.post(this.buildEndPoint(endPoint), params, {headers, responseType: 'blob'});
  }

  public getBlob(endPoint: string, params: any, headers?: HttpHeaders): Observable<any> {
    return this.httpClient.get(this.buildEndPoint(endPoint), {params, headers, responseType: 'blob'});
  }

  public getArrayBuffer(endPoint: string, params: any, headers?: HttpHeaders): Observable<any> {
    return this.httpClient.get(this.buildEndPoint(endPoint), {params, headers, responseType: 'arraybuffer'});
  }

  public put(endPoint: string, params?: any): Observable<any> {
    return this.httpClient.put(this.buildEndPoint(endPoint), params);
  }

  public delete(endPoint: string, id: string): Observable<any> {
   return this.httpClient.delete(this.buildEndPoint(endPoint), { params: { id } });
  }

  private buildEndPoint(endPoint: string): string {
    const url = this.env.url;
    return `${url}/${endPoint}`;
  }

  private dispatchResponseHeaders(headers: HttpHeaders): void {
    this.responseHeaders.next(headers);
  }

  private getWithPagination(endPoint: string, params?: any, headers?: HttpHeaders): Observable<any> {
    const page = of({params, apiConnector: this, endPoint});

    return combineLatest([this.getWithoutPagination(endPoint, params, headers), page]);
  }

  private getWithoutPagination(endPoint: string, params?: any, headers?: HttpHeaders): Observable<any> {
    return this.httpClient.get(
      this.buildEndPoint(endPoint), { params, headers, observe: 'response' })
        .pipe(
          tap((response: HttpResponse<any>) => this.dispatchResponseHeaders(response.headers)),
          map((response: HttpResponse<any>) => response.body),
          tap((response) => this.cacheService.keep({url: endPoint, data: response})),
        );
  }
}
